import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { API_BASE_URL, API_ENDPOINTS } from '../../config';
import { track } from '../../utils/analytics';
import '../../styles/auth.css';

interface RestoreForm {
  email: string;
}

const Restore: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RestoreForm>({ email: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const resetToken = new URLSearchParams(window.location.search).get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);

  useEffect(() => {
    document.title = 'OfferFlow | Restore Password';
  }, []);

  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const userUid = localStorage.getItem('user_uid');
        if (!token || !userUid) return;

        await api.get(API_ENDPOINTS.me);
        navigate('/welcome', { replace: true });
      } catch {
      }
    };

    checkExistingSession();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/v1/password-reset`, {
        method: 'POST',
        headers: { 'accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });

      if (response.ok) {
        track('password_reset_requested');
        setSuccess(true);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to send reset email');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/v1/confirm-password-reset`, {
        method: 'POST',
        headers: { 'accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      });

      if (response.ok) {
        track('password_reset_confirmed');
        navigate('/sign-in', { replace: true });
      } else {
        const errorData = await response.json();
        if (response.status === 400) {
          setTokenExpired(true);
        } else {
          setError(errorData.detail || 'Something went wrong');
        }
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    navigate('/sign-in');
  };

  if (tokenExpired) {
    return (
      <div className="auth-container">
        <div className="auth-left">
          <div className="auth-card">
            <div className="auth-header">
              <h1>Link Expired</h1>
              <p>This password reset link has expired or is invalid.</p>
            </div>
            <div className="auth-footer">
              <button onClick={() => navigate('/restore', { replace: true })} className="auth-button">
                Request a new link
              </button>
            </div>
          </div>
        </div>
        <div className="auth-right">
          <div className="auth-visual auth-visual--restore">
            <div className="auth-visual-icon">
              <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="60" cy="60" r="40" stroke="#ef4444" strokeWidth="3"/>
                <path d="M45 45 L75 75 M75 45 L45 75" stroke="#ef4444" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="auth-visual-text">
              <h3>Link Expired</h3>
              <p>Reset links are valid for 1 hour</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (resetToken) {
    return (
      <div className="auth-container">
        <div className="auth-left">
          <div className="auth-card">
            <div className="auth-header">
              <h1>Set New Password</h1>
              <p>Enter your new password below</p>
            </div>

            <form onSubmit={handleConfirmReset} className="auth-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Enter new password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm new password"
                />
              </div>

              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? 'Saving...' : 'Set New Password'}
              </button>
            </form>
          </div>
        </div>

        <div className="auth-right">
          <div className="auth-visual auth-visual--restore">
            <div className="auth-visual-icon">
              <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="35" y="55" width="50" height="35" rx="4" stroke="#d1d5db" strokeWidth="2" fill="none"/>
                <path d="M45 55 V45 a15 15 0 0 1 30 0 V55" stroke="#111827" strokeWidth="2" fill="none" strokeLinecap="round"/>
                <circle cx="60" cy="72" r="4" fill="#111827"/>
              </svg>
            </div>
            <div className="auth-visual-text">
              <h3>Set New Password</h3>
              <p>Choose a strong password for your account</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-left">
          <div className="auth-card">
            <div className="auth-header">
              <h1>Check Your Email</h1>
              <p>We've sent a password reset link to {formData.email}</p>
            </div>

            <div className="success-content">
              <div className="success-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="2"/>
                  <path d="m9 12 2 2 4-4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p className="success-text">
                Please check your email and click on the password reset link to continue.
              </p>
              <p className="success-subtext">
                Didn't receive the email? Check your spam folder or try again.
              </p>
            </div>

            <div className="auth-footer">
              <button 
                onClick={handleBackToSignIn}
                className="auth-button secondary"
              >
                Back to Sign In
              </button>
              <p className="auth-footer-row">
                <span>Remember your password?</span>
                <Link to="/sign-in" className="auth-link">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
        
        <div className="auth-right">
          <div className="auth-visual auth-visual--restore">
            <div className="auth-visual-icon">
                <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="60" cy="60" r="40" stroke="#16a34a" strokeWidth="3"/>
                  <path d="M45 60 L55 70 L75 50" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="25" y="25" width="70" height="70" rx="8" stroke="#e5e7eb" strokeWidth="1.5" fill="none"/>
                </svg>
            </div>
            <div className="auth-visual-text">
              <h3>Check Your Email</h3>
              <p>Password reset link has been sent</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-left">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Reset Password</h1>
            <p>Enter your email address and we'll send you a link to reset your password</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Enter your email address"
              />
            </div>

            <button 
              type="submit" 
              className="auth-button"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <div className="auth-footer">
            <p className="auth-footer-row">
              <span>Remember your password?</span>
              <Link to="/sign-in" className="auth-link">
                Back to Sign In
              </Link>
            </p>
            <p className="auth-footer-row">
              <span>Don't have an account?</span>
              <Link to="/sign-up" className="auth-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      <div className="auth-right">
        <div className="auth-visual auth-visual--restore">
          <div className="auth-visual-icon">
                <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="30" y="40" width="60" height="45" rx="4" stroke="#d1d5db" strokeWidth="2" fill="none"/>
                  <path d="M30 50 L60 65 L90 50" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="45" cy="55" r="2" fill="#111827"/>
                  <circle cx="60" cy="60" r="2" fill="#111827"/>
                  <circle cx="75" cy="55" r="2" fill="#111827"/>
                  <path d="M50 30 L70 30 M60 20 L60 30" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M40 70 L80 70" stroke="#e5e7eb" strokeWidth="1.5" strokeDasharray="2 2"/>
                </svg>
          </div>
          <div className="auth-visual-text">
            <h3>Reset Password</h3>
            <p>We'll send you a secure link to reset your password</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Restore;
